import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AdminShell from "../Components/admin-shell/AdminShell";
import { useAuth } from "../utils/auth";
import { isOwner } from "../utils/roles";
import { createAccessRole, fetchAccessCatalog, saveRolePermissions } from "../access/permissions";

export default function AccessRolesPage() {
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const [roleKey, setRoleKey] = useState("ProductionManager");
  const [draft, setDraft] = useState([]);
  const [copy, setCopy] = useState({ key: "", nameAr: "", copyFrom: "ProductionWorker" });
  const catalog = useQuery({
    queryKey: ["accessCatalog"],
    queryFn: fetchAccessCatalog,
    enabled: isOwner(authState.role),
  });

  const selected = useMemo(
    () => (catalog.data?.roles || []).find((role) => role.key === roleKey),
    [catalog.data, roleKey]
  );

  const choose = (key) => {
    const role = (catalog.data?.roles || []).find((item) => item.key === key);
    setRoleKey(key);
    setDraft(role?.permissions || []);
  };

  if (!isOwner(authState.role)) {
    return (
      <AdminShell>
        <p className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-700">إدارة الصلاحيات متاحة للمالك فقط.</p>
      </AdminShell>
    );
  }

  const grouped = {};
  (catalog.data?.permissions || []).forEach((item) => {
    grouped[item.module] = grouped[item.module] || [];
    grouped[item.module].push(item);
  });

  return (
    <AdminShell>
      <div className="space-y-4" dir="rtl">
        <h1 className="text-2xl font-extrabold text-slate-900">الأدوار والصلاحيات</h1>
        <p className="text-sm text-slate-500">
          أدوار المالك والمدير والمشرف والمتفرج ثابتة. القوالب المستقبلية ظاهرة وغير قابلة للتعيين حتى يُفعّل القسم.
          تغيير صلاحية دور الإنتاج يسري في الطلب التالي. تغيير دور المستخدم نفسه يحتاج تسجيل دخول جديد لأن الدور داخل رمز الدخول.
        </p>
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-3">
            {(catalog.data?.roles || []).map((role) => (
              <button
                key={role.key}
                type="button"
                onClick={() => choose(role.key)}
                className={`mb-1 block w-full rounded-xl px-3 py-2 text-right text-sm ${role.key === roleKey ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}
              >
                <span className="block font-bold">{role.nameAr}</span>
                <span className="text-xs opacity-70">{role.isSystem ? "أساسي" : role.isTemplate ? "قالب غير مفعّل" : "قابل للتعديل"}</span>
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            {!selected && <p className="text-sm text-slate-500">اختر دوراً.</p>}
            {selected && (
              <>
                <h2 className="mb-3 font-bold">{selected.nameAr}</h2>
                {Object.entries(grouped).map(([module, items]) => (
                  <div key={module} className="mb-4">
                    <h3 className="mb-2 text-sm font-bold text-slate-500">{module}</h3>
                    <div className="grid gap-2 md:grid-cols-2">
                      {items.map((item) => {
                        const checked = (selected.isSystem ? selected.permissions : draft).includes(item.key);
                        return (
                          <label key={item.key} className="flex items-start gap-2 rounded-xl border border-slate-100 p-2 text-sm">
                            <input
                              type="checkbox"
                              className="mt-1"
                              disabled={selected.isSystem || selected.isTemplate}
                              checked={checked}
                              onChange={(event) => {
                                setDraft((current) => event.target.checked
                                  ? [...current, item.key]
                                  : current.filter((key) => key !== item.key));
                              }}
                            />
                            <span>
                              <span className="block font-semibold">{item.nameAr}</span>
                              <span className="text-xs text-slate-500">{item.description}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {!selected.isSystem && !selected.isTemplate && (
                  <button
                    type="button"
                    className="rounded-xl bg-slate-900 px-4 py-2 text-sm text-white"
                    onClick={async () => {
                      try {
                        await saveRolePermissions(selected.key, draft);
                        toast.success("تم حفظ الصلاحيات");
                        queryClient.invalidateQueries({ queryKey: ["accessCatalog"] });
                      } catch (error) {
                        toast.error(error.message);
                      }
                    }}
                  >
                    حفظ
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <form
          className="grid gap-2 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-4"
          onSubmit={async (event) => {
            event.preventDefault();
            try {
              await createAccessRole(copy);
              toast.success("تم إنشاء الدور بدون صلاحيات حساسة");
              setCopy({ key: "", nameAr: "", copyFrom: "ProductionWorker" });
              queryClient.invalidateQueries({ queryKey: ["accessCatalog"] });
            } catch (error) {
              toast.error(error.message);
            }
          }}
        >
          <input className="rounded-xl border px-3 py-2" placeholder="مفتاح الدور" value={copy.key} onChange={(event) => setCopy({ ...copy, key: event.target.value })} />
          <input className="rounded-xl border px-3 py-2" placeholder="الاسم بالعربية" value={copy.nameAr} onChange={(event) => setCopy({ ...copy, nameAr: event.target.value })} />
          <select className="rounded-xl border px-3 py-2" value={copy.copyFrom} onChange={(event) => setCopy({ ...copy, copyFrom: event.target.value })}>
            <option value="">بدون نسخ</option>
            {(catalog.data?.roles || []).filter((role) => !role.isSystem).map((role) => (
              <option key={role.key} value={role.key}>{role.nameAr}</option>
            ))}
          </select>
          <button type="submit" className="rounded-xl border px-3 py-2 text-sm">نسخ دور جديد</button>
        </form>
      </div>
    </AdminShell>
  );
}
